import { useCallback, useEffect, useState } from 'react'
import { AlertCircle, Plus } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useIsViewer, VIEWER_NO_PERMISSION } from '@/hooks/useIsViewer'
import { trackingAdminService, type AdminPixel } from '@/services/trackingAdmin'
import PixelsCard from './PixelsCard'
import LogsCard from './LogsCard'

/**
 * Backoffice > Meta Pixel Tracking: the pixels that receive ROASForm's OWN
 * conversions (Lead on signup, StartTrial and Subscribe on billing) through the
 * Conversions API, plus the delivery log. Browser pixels stay in GTM; this page
 * only controls the server-side half. The pixel ids here must match the ones
 * GTM fires.
 */
export default function TrackingPage() {
  const isViewer = useIsViewer()
  const [pixels, setPixels] = useState<AdminPixel[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState('overview')
  // The Add button lives in the page header, but the one modal instance that
  // serves both add and edit stays in PixelsCard, so the flag is passed down.
  const [creating, setCreating] = useState(false)

  const load = useCallback(async () => {
    try {
      setPixels(await trackingAdminService.listPixels())
      setError(null)
    } catch (e: any) {
      setError(e?.response?.data?.message || "Couldn't load pixels")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  return (
    <div className="space-y-4 sm:space-y-6 w-full max-w-[1310px] mx-auto px-4 sm:px-0 animate-in fade-in duration-300">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Meta Pixel Tracking</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Server-side Meta Conversions API for the signup and trial funnels. Browser pixels are managed in GTM.
          </p>
        </div>
        {tab === 'overview' && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="w-full sm:w-auto">
                <Button onClick={() => setCreating(true)} disabled={isViewer} className="w-full sm:w-auto">
                  <Plus />
                  Add pixel
                </Button>
              </span>
            </TooltipTrigger>
            {isViewer && <TooltipContent>{VIEWER_NO_PERMISSION}</TooltipContent>}
          </Tooltip>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setCreating(false) }} className="w-full">
        <TabsList>
          <TabsTrigger value="overview">
            Overview
          </TabsTrigger>
          <TabsTrigger value="logs">
            Logs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <PixelsCard
            pixels={pixels}
            loading={loading}
            onChanged={setPixels}
            creating={creating}
            onCreatingChange={setCreating}
          />
        </TabsContent>

        <TabsContent value="logs" className="mt-4">
          <LogsCard pixels={pixels} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
